import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import { Print } from '@mui/icons-material';
import { useRouter } from '../contexts/RouterContext';
import { useAuth } from '../contexts/AuthContext';
import { patientsService, PatientDetail } from '../services/patientsService';
import { printTemplateService, renderTemplateHtml, PrintTemplate } from '../services/printTemplateService';
import { PrintTemplateBar, usePrintTemplateChoice } from './print/PrintTemplateChoice';
import { colors } from '../theme/designSystem';
import { CUSTOM_KIND } from './modals/DocumentModal';

/**
 * Impressão de documentos do paciente (?page=documents-print&patientId=): cabeçalho e rodapé vêm
 * do TEMPLATE PADRÃO da entidade, repetidos em toda página (thead/tfoot, mesmo esquema das
 * evoluções). Enquanto a emissão não tem API (item 64), o conteúdo chega pela sessionStorage
 * gravada pela aba Documentos — um documento por página.
 */
export const DOCUMENTS_PRINT_KEY = 'clinic4us-documents-print';

export interface PrintableDocument {
  id: string;
  date: string;
  kind: string;
  /** HTML do editor da aba Documentos */
  content: string;
  issuer: string;
}


const DocumentsPrint: React.FC = () => {
  const { getParam } = useRouter();
  const { user } = useAuth();
  const patientId = getParam('patientId') || '';

  const [patient, setPatient] = useState<PatientDetail | null>(null);
  const [docs, setDocs] = useState<PrintableDocument[]>([]);
  const [templates, setTemplates] = useState<PrintTemplate[]>([]);
  const templateChoice = usePrintTemplateChoice(templates, 'documents');
  const template = templateChoice.template;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    document.title = 'Clinic4Us - Impressão de Documentos';
    const load = async () => {
      try {
        let selected: PrintableDocument[] = [];
        try { selected = JSON.parse(sessionStorage.getItem(DOCUMENTS_PRINT_KEY) || '[]'); } catch { selected = []; }
        const [patientData, templates] = await Promise.all([
          patientsService.getById(patientId),
          user?.entityId ? printTemplateService.getByEntity(user.entityId).catch(() => []) : Promise.resolve([]),
        ]);
        setPatient(patientData);
        setDocs(selected);
        setTemplates(templates);
      } catch {
        setError('Não foi possível carregar os dados para impressão.');
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: colors.primary }} />
      </Box>
    );
  }

  if (error || !patient) {
    return (
      <Typography sx={{ color: colors.error, textAlign: 'center', py: 8 }}>
        {error || 'Paciente não encontrado.'}
      </Typography>
    );
  }


  const templateFooter = template ? (
    <div
      style={{ textAlign: 'center', fontSize: '9pt', lineHeight: 1.4, paddingTop: '4mm', borderTop: '1px solid #ddd' }}
      dangerouslySetInnerHTML={{ __html: renderTemplateHtml(template.footer) }}
    />
  ) : null;

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5', padding: '2rem', '@media print': { padding: 0, backgroundColor: '#fff' } }}>
      <style>{`
        @media print {
          /* Margem da folha zerada (sem dados do navegador no papel). Topo e pé de cada folha vêm do
             thead/tfoot, que se repetem; laterais, do padding. Cada documento começa em folha nova e,
             se for longo, continua nas seguintes com o mesmo cabeçalho e rodapé (esquema do contrato). */
          @page { size: A4 portrait; margin: 0; }
          .documents-print-page { padding: 0 15mm !important; width: auto !important; min-height: 0 !important; box-shadow: none !important; margin: 0 !important; break-after: page; }
          .documents-print-page:last-of-type { break-after: auto; }
          .documents-frame { width: 100%; border-collapse: collapse; }
          .documents-frame > thead { display: table-header-group; }
          .documents-frame > tfoot { display: table-footer-group; }
          .documents-frame > thead > tr > td,
          .documents-frame > tbody > tr > td,
          .documents-frame > tfoot > tr > td { padding: 0; }
          .documents-frame-top { display: block !important; height: 12mm; }
          .documents-frame-footer-space { display: block !important; height: 30mm; }
          .documents-footer-screen { display: none !important; }
          .documents-footer-print { display: block !important; position: fixed; left: 15mm; right: 15mm; bottom: 8mm; }
          .print-frame-header img { max-height: 18mm !important; }
        }
      `}</style>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2, maxWidth: '210mm', mx: 'auto', '@media print': { display: 'none' } }}>
        <PrintTemplateBar choice={templateChoice} />
        <Button
          variant="contained"
          startIcon={<Print />}
          onClick={() => window.print()}
          sx={{ backgroundColor: colors.primary, textTransform: 'none', '&:hover': { backgroundColor: '#029AAB' } }}
        >
          Imprimir
        </Button>
      </Box>

      {docs.length === 0 && (
        <Typography sx={{ textAlign: 'center', color: colors.textSecondary, py: 6 }}>
          Nenhum documento selecionado para impressão. Volte à aba Documentos e use o botão de imprimir.
        </Typography>
      )}

      {docs.map((doc) => (
        <Box
          key={doc.id}
          className="documents-print-page"
          sx={{
            width: '210mm', maxWidth: '100%', minHeight: '297mm', padding: '15mm', paddingBottom: '35mm', boxSizing: 'border-box',
            background: 'white', margin: '0 auto 2rem', position: 'relative', boxShadow: '0 0 8px rgba(0,0,0,0.15)', fontSize: '12pt', color: '#000',
          }}
        >
          <table className="documents-frame" style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            <thead><tr><td>
              <div className="documents-frame-top" style={{ display: 'none' }} />
              {template && (
                <div className="print-frame-header">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10mm' }}>
                    <div style={{ flexShrink: 0 }}>
                      {template.file ? <img src={template.file} alt="logo" style={{ maxHeight: '30mm', maxWidth: '70mm', objectFit: 'contain' }} /> : null}
                    </div>
                    <div style={{ textAlign: 'right', fontSize: '10pt', lineHeight: 1.5 }} dangerouslySetInnerHTML={{ __html: renderTemplateHtml(template.header) }} />
                  </div>
                  <hr style={{ border: 'none', borderTop: '1px solid #999', margin: '6mm 0' }} />
                </div>
              )}
            </td></tr></thead>

            <tfoot><tr><td>
              <div className="documents-frame-footer-space" style={{ display: 'none' }} />
            </td></tr></tfoot>

            <tbody><tr><td>
              {doc.kind !== CUSTOM_KIND && (
                <Box sx={{ textAlign: 'center', mb: '8mm' }}>
                  <Typography sx={{ fontSize: '14pt', fontWeight: 700, color: '#212529', textTransform: 'uppercase' }}>{doc.kind}</Typography>
                </Box>
              )}
              {/* Sem bloco fixo de paciente e data (RN-537s): a identificação vem do corpo, pelas variáveis
                  do texto padrão, e acompanha a data do documento — que pode ser retroativa */}
              <Box
                sx={{ fontSize: '12pt', color: '#000', lineHeight: 1.6, '& p': { m: 0, mb: '2mm' }, '& ul, & ol': { my: '2mm', pl: '8mm' } }}
                dangerouslySetInnerHTML={{ __html: doc.content }}
              />
              {/* Sem linha fixa de assinatura (RN-537s): a assinatura é parte do texto padrão, que pode trazer
                  {emissor.nome} abaixo da linha — com a linha fixa, o nome ficaria acima dela */}
            </td></tr></tbody>
          </table>

          {/* Na tela, no pé de cada documento; no papel, o rodapé fixo abaixo vale para todas as folhas */}
          {templateFooter && (
            <div className="documents-footer-screen" style={{ position: 'absolute', left: '15mm', right: '15mm', bottom: '10mm' }}>
              {templateFooter}
            </div>
          )}
        </Box>
      ))}

      {templateFooter && docs.length > 0 && (
        <div className="documents-footer-print" style={{ display: 'none' }}>{templateFooter}</div>
      )}
    </Box>
  );
};

export default DocumentsPrint;
